import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { OrderType, PositionsSortByType, TimeFilterType } from '@/shared/backend/types/leaderboard'

import { Option, ReactSelect } from 'components/ReactSelect/ReactSelect'
import Checkbox from 'components/Checkbox/Checkbox'
import { Typography } from 'components/Typography/Typography'
import { Button } from 'components/Button/Button'
import { EntityAutocomplete } from 'components/Autocomplete/EntityAutocomplete'
import { PAGE_SIZE, positionsSortMobileOptions, timeFilterOptions } from '../const'
import { useLeaderboardPositions } from '../hooks/useLeaderboardPositions'
import { Banner } from 'components/Banner/Banner'
import { useIsMobile } from 'utils/useIsMobile'
import { paginate } from 'components/Pagination/Pagination'
import { getPositionsAutocompleteData } from '../utils'
import { PositionsTable } from './PositionsTable'
import { PositionsFilters } from './PositionsFilter'
import { PositionsList } from './PositionsList'
import { usePostHogEntityViewed } from 'utils/usePostHogEntityViewed'

const Container = styled.div`
  margin-top: 32px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 20px;
  }
`

const ContainerControls = styled.div<{ $isLoading: boolean }>`
  ${p =>
    p.$isLoading &&
    `
  opacity: 0.5;
  pointer-events: none;
  `}
`

const PositionControls = styled.div<{ $isLoading: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${p =>
    p.$isLoading &&
    `
  opacity: 0.5;
  pointer-events: none;
  `}
`

const CheckboxContainer = styled.div`
  display: inline-flex;
  margin-right: 32px;
  align-items: center;
  cursor: pointer;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-right: 0;
  }
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  button {
    margin-right: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const FILTERS_KEY = 'leaderboard-positions-filters'

const SORT_BY_KEY = 'sort-by'
const SORT_ORDER_KEY = 'sort-order'
const TIME_FILTER_KEY = 'time-filter'
const SHOW_CLOSED_POSITIONS_KEY = 'show-closed-positions'
const SHOW_STABLE_KEY = 'show-stable'
const SHOW_VOLATILE_KEY = 'show-volatile'

const ORDER_TYPES: OrderType[] = ['asc', 'desc']
const SORT_ORDER_TYPES: PositionsSortByType[] = [
  'pnl',
  'aprFull',
  'returnOnEquity',
  'hodlBenchmarkPnl',
  'timeWeightedEquity',
  'positionCreationTimestampMs',
]
const TIME_FILTER_TYPES = timeFilterOptions.map(o => o.value)

export const Positions = () => {
  usePostHogEntityViewed('leaderboard:positions_view')

  const [sortBy, setSortBy] = useState<PositionsSortByType>('aprFull')
  const [sortOrder, setSortOrder] = useState<OrderType>('desc')
  const [showClosedPositions, setShowClosedPositions] = useState(true)
  const [showStable, setShowStable] = useState(false)
  const [showVolatile, setShowVolatile] = useState(false)

  const [selectedPositionsData, setSelectedPositionData] = useState<string[]>([])
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(timeFilterOptions[0])
  const [currentPage, setCurrentPage] = useState(1)

  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const savedFilters = localStorage.getItem(FILTERS_KEY)
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters)
      const sortByLS = parsedFilters[SORT_BY_KEY]
      if (sortByLS && SORT_ORDER_TYPES.indexOf(sortByLS) !== -1) {
        setSortBy(sortByLS)
      }
      const sortOrderLS = parsedFilters[SORT_ORDER_KEY]
      if (sortOrderLS && ORDER_TYPES.indexOf(sortOrderLS) !== -1) {
        setSortOrder(sortOrderLS)
      }
      const timeFilterLs = parsedFilters[TIME_FILTER_KEY]
      if (timeFilterLs && TIME_FILTER_TYPES.indexOf(timeFilterLs) !== -1) {
        const timeFilterOption = timeFilterOptions.find(o => o.value === timeFilterLs)
        if (timeFilterOption) {
          setSelectedTimeFilter(timeFilterOption)
        }
      }
      const showClosedLs = parsedFilters[SHOW_CLOSED_POSITIONS_KEY]
      if (String(showClosedLs) === 'true' || String(showClosedLs) === 'false') {
        setShowClosedPositions(showClosedLs)
      }
      const showStableLs = parsedFilters[SHOW_STABLE_KEY]
      const showVolatileLs = parsedFilters[SHOW_VOLATILE_KEY]

      if (String(showStableLs) === 'true' || String(showStableLs) === 'false') {
        setShowStable(showStableLs)
      }
      if (String(showVolatileLs) === 'true' || String(showVolatileLs) === 'false') {
        setShowVolatile(showVolatileLs)
      }

      // Both can't be true at same time
      if (String(showStableLs) === 'true' && String(showVolatileLs) === 'true') {
        setShowStable(true)
        setShowVolatile(false)
      }
    }
    setHydrated(true)
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder, selectedTimeFilter, selectedPositionsData, showClosedPositions, showStable, showVolatile])

  useEffect(() => {
    if (!hydrated) {
      return
    }
    localStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({
        [SORT_BY_KEY]: sortBy,
        [SORT_ORDER_KEY]: sortOrder,
        [TIME_FILTER_KEY]: selectedTimeFilter.value,
        [SHOW_CLOSED_POSITIONS_KEY]: showClosedPositions,
        [SHOW_STABLE_KEY]: showStable,
        [SHOW_VOLATILE_KEY]: showVolatile,
      })
    )
  }, [hydrated, sortBy, sortOrder, selectedTimeFilter, showClosedPositions, showStable, showVolatile])

  const { data, isLoading, isError } = useLeaderboardPositions(
    hydrated,
    sortBy,
    sortOrder,
    selectedTimeFilter.value as TimeFilterType,
    showClosedPositions ? 'closed' : 'open',
    showStable ? true : showVolatile ? false : null
  )

  const autocompleteData = getPositionsAutocompleteData(data)

  const isMobile = useIsMobile()

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      if (isMobile) {
        window.scrollTo(0, 0)
      }
    },
    [isMobile]
  )
  const handleSelectTimeFilterChange = useCallback((newValue: Option) => {
    setSelectedTimeFilter(newValue)
  }, [])

  const handleWalletIdsChange = useCallback((values: string[]) => {
    setSelectedPositionData(values)
  }, [])

  const handleShowClosedPositionsChange = useCallback(() => {
    setShowClosedPositions(!showClosedPositions)
  }, [showClosedPositions])

  const handleShowStableChange = useCallback(() => {
    setShowStable(!showStable)
    if (!showStable) {
      setShowVolatile(false)
    }
  }, [showStable])

  const handleShowVolatileChange = useCallback(() => {
    setShowVolatile(!showVolatile)
    if (!showVolatile) {
      setShowStable(false)
    }
  }, [showVolatile])

  const handleColumnClick = useCallback(
    (columnKey: PositionsSortByType) => {
      if (columnKey === sortBy) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
      } else {
        setSortOrder('desc')
      }
      setSortBy(columnKey)
    },
    [sortBy, sortOrder]
  )

  const handleSubmitFilters = useCallback(
    (newValue: Option, selectedSort: string, showClosed: boolean, showStable: boolean, showVolatile: boolean) => {
      const sortOption = selectedSort.split(' ')[0] as PositionsSortByType
      const isDesc = selectedSort.indexOf('(desc)') !== -1
      if (isDesc) {
        setSortOrder('desc')
      } else {
        setSortOrder('asc')
      }
      setSortBy(sortOption)
      setSelectedTimeFilter(newValue)
      setShowClosedPositions(showClosed)
      setShowStable(showStable)
      setShowVolatile(showVolatile)
    },
    []
  )

  const filteredData = useMemo(() => {
    if (!selectedPositionsData.length) {
      return data
    }
    let newData = [...data]
    for (const selected of selectedPositionsData) {
      newData = newData.filter(
        d =>
          d.positionId === selected ||
          d.ownerId === selected ||
          d.displayInfo.position.X.typeName === selected ||
          d.displayInfo.position.Y.typeName === selected
      )
    }

    return newData
  }, [selectedPositionsData, data])

  const paginatedItems = paginate(filteredData || [], PAGE_SIZE, currentPage)

  const totalPages = Math.ceil((filteredData || []).length / PAGE_SIZE)

  if (isError) {
    return (
      <Container>
        <Banner variant="warning">
          <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
            Data not available
          </Typography>
        </Banner>
      </Container>
    )
  }

  return (
    <Container>
      {isMobile ? (
        <ContainerControls $isLoading={isLoading}>
          <TitleContainer style={{ marginBottom: 12 }}>
            <Typography variant="label1" fontWeight="semiBold">
              Positions
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" style={{ marginRight: 8 }}>
                Sort: {positionsSortMobileOptions.find(o => o.value === `${sortBy} (${sortOrder})`)?.label}
              </Typography>
              <PositionsFilters
                initialTimeFilter={selectedTimeFilter}
                onSubmit={handleSubmitFilters}
                sortBy={sortBy}
                sortOrder={sortOrder}
                initialShowStable={showStable}
                initialShowVolatile={showVolatile}
                initialShowClosedPositions={showClosedPositions}
              />
            </div>
          </TitleContainer>
          <EntityAutocomplete
            options={autocompleteData}
            value={selectedPositionsData}
            onChange={handleWalletIdsChange}
            placeholder="Search by token, wallet or position ID..."
          />
        </ContainerControls>
      ) : (
        <PositionControls $isLoading={isLoading}>
          <EntityAutocomplete
            options={autocompleteData}
            value={selectedPositionsData}
            onChange={handleWalletIdsChange}
            placeholder="Search by token, wallet or position ID..."
          />
          <FilterControls>
            <CheckboxContainer onClick={handleShowClosedPositionsChange}>
              <Checkbox checked={showClosedPositions} />
              <Typography variant="body" color="secondary" style={{ marginLeft: 6 }}>
                Show closed positions
              </Typography>
            </CheckboxContainer>
            <Button outlined={!showStable} onClick={handleShowStableChange} style={isMobile ? {} : { height: 46 }}>
              Stable
            </Button>
            <Button outlined={!showVolatile} onClick={handleShowVolatileChange} style={isMobile ? {} : { height: 46 }}>
              Volatile
            </Button>
            <ReactSelect
              options={timeFilterOptions}
              selectedOption={selectedTimeFilter}
              onSelect={handleSelectTimeFilterChange}
            />
          </FilterControls>
        </PositionControls>
      )}

      {isMobile ? (
        <PositionsList
          paginatedItems={paginatedItems}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          isLoading={isLoading}
        />
      ) : (
        <PositionsTable
          sortBy={sortBy}
          handleColumnClick={handleColumnClick}
          sortOrder={sortOrder}
          paginatedItems={paginatedItems}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          isLoading={isLoading}
        />
      )}
    </Container>
  )
}
